import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute = ({ children, module }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    // Super Admin bypasses all checks immediately
    if (user?.user_type === 'super_admin') {
      setHasAccess(true);
      setLoading(false);
      return;
    }

    const role = user?.user_type || 'worker';
    
    // Define module access for different roles
    const moduleAccess = {
      'super_admin': ['all'],
      'master_admin': ['dashboard', 'inventory', 'procurement', 'production', 'sales', 'dispatch', 'finance', 'hr', 'attendance', 'bulk-attendance', 'shifts', 'attendance-reports', 'gst', 'users', 'reports', 'payment-status'],
      'manager': ['dashboard', 'inventory', 'procurement', 'production', 'sales', 'dispatch', 'finance', 'hr', 'attendance', 'bulk-attendance', 'shifts', 'attendance-reports', 'gst', 'reports', 'payment-status'],
      'supervisor': ['dashboard', 'inventory', 'production', 'dispatch', 'hr', 'attendance', 'bulk-attendance', 'shifts', 'attendance-reports', 'gst', 'reports'],
      'accountant': ['dashboard', 'procurement', 'sales', 'finance', 'reports', 'attendance-reports', 'gst', 'payment-status'],
      'worker': ['dashboard', 'inventory', 'production', 'attendance'],
      'driver': ['dashboard', 'dispatch']
    };

    const allowedModules = moduleAccess[role] || ['dashboard'];
    
    if (module) {
      const access = allowedModules.includes('all') || allowedModules.includes(module);
      setHasAccess(access);
    } else {
      const path = window.location.pathname;
      const routeModuleMap = {
        '/inventory': 'inventory',
        '/procurement': 'procurement',
        '/production': 'production',
        '/sales': 'sales',
        '/dispatch': 'dispatch',
        '/finance': 'finance',
        '/hr': 'hr',
        '/attendance': 'attendance',
        '/bulk-attendance': 'bulk-attendance',
        '/shifts': 'shifts',
        '/attendance-reports': 'attendance-reports',
        '/gst': 'gst',
        '/payment-status': 'payment-status',
        '/users': 'users',
        '/permissions': 'permissions',
        '/reports': 'reports',
        '/roles': 'roles'
      };
      
      let requiredModule = null;
      for (const [route, moduleName] of Object.entries(routeModuleMap)) {
        if (path.startsWith(route)) {
          requiredModule = moduleName;
          break;
        }
      }
      
      if (requiredModule) {
        const access = allowedModules.includes('all') || allowedModules.includes(requiredModule);
        setHasAccess(access);
      } else {
        setHasAccess(true);
      }
    }
    setLoading(false);
  }, [user, module]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!hasAccess) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

export default ProtectedRoute;